import React, { FC, useEffect } from "react";
import * as Sentry from "@sentry/react";
import Button from "components/Button/Button";
import { ButtonColor, ButtonSize, ButtonVariant } from "enums/Button";
import { useTranslation } from "react-i18next";
import { useRouteError } from "react-router-dom";
import { ErrorContent, ErrorWrapper } from "./RouteErrorScreen.styles";

// `errorElement` of the root route in src/index.jsx: rendered by the router in
// place of the whole route tree when anything in it throws during render — a
// component bug, or a chunk that is still failing after lazyWithRetry's retry
// and one-shot reload. Without it react-router shows its built-in developer
// page ("Unexpected Application Error!" plus a stack trace).
//
// This is the app's only error boundary. The data router wraps the root route
// in its own RenderErrorBoundary, so a boundary placed around <RouterProvider>
// never sees these errors. It also recovers on navigation: the router clears
// the error when the location changes, so browser Back leaves this screen.
const RouteErrorScreen: FC = () => {
  const error = useRouteError();
  const { t } = useTranslation("common");

  useEffect(() => {
    console.error("RouteErrorScreen caught error:", error);
    Sentry.captureException(error);
  }, [error]);

  return (
    <ErrorWrapper>
      <ErrorContent>
        <h2>{t("appError.title")}</h2>
        <p>{t("appError.description")}</p>
        <Button
          onClick={() => window.location.reload()}
          color={ButtonColor.Primary}
          variant={ButtonVariant.Filled}
          size={ButtonSize.Large}
        >
          {t("appError.reload")}
        </Button>
      </ErrorContent>
    </ErrorWrapper>
  );
};

export default RouteErrorScreen;
